import {ILocaProcessor} from "../../api/ILocaProcessor";
import {LanguageVO} from "../../../vos/LanguageVO";
import {ProcessorTypeConstants} from "../../../constants/ProcessorTypeConstants";
import {TextsParser} from "../../../parsers/TextsParser";
import {LanguageData} from "../../../parsers/LanguageMetaData";
import {AbstractTextContentVO} from "../../../vos/AbstractTextContentVO";
import {DateTimeStyle} from "../../../globalization/DateTimeStyle";
import {CLDRJSONData} from "../../../cldr/DefaultCLDRJsonData";
import {URLLoaderService} from "./URLLoaderService";
import {LeastRecentUsedCache} from "../../../utils/LeastRecentlyUsedCache";
import {getLogger} from "@goodgamestudios/logger";
import {reversePunctuation, reverseSinglePlaceholder} from "../../../utils/ArabicBugfixes";


const logger = getLogger("LocalizationJS.GlobalizeTextProcessor");

// tslint:disable-next-line:variable-name
const Globalize = require("globalize");

// disable plural generator, it is not used for us so don't generate function on runtime
const emptyFunction = () => {};
Globalize.pluralGenerator = Globalize.prototype.pluralGenerator = function (options) {
    return emptyFunction;
};

export class GlobalizeTextProcessor implements ILocaProcessor {
    private _initialized: boolean = false;
    private _localizeReplacements: boolean = false;
    private _languageVO: LanguageVO;
    private loadedLocales: Set<string> = new Set(["en", "de"]);
    private _textsVersionNo: string;
    private _textsDeployTime: string;
    private _numberFormatter;
    private _currencyFormatter: (value: number) => string;
    private _currencyCode: string;
    private _currencySymbol: string;

    private _integerFormatter;
    private _decimalFormatter;

    private cachedTranslations: LeastRecentUsedCache = new LeastRecentUsedCache();

    constructor(private _cldrURL, private _isCaseSensitive: boolean = true) {
        this._isCaseSensitive = _isCaseSensitive;
    }


    private set languageVO(value: LanguageVO) {
        this._languageVO = value;
    }

    /**
     * initialize
     * @param languageVO language object with language and country as well as number formats
     * @param texts the translations JSON data
     * @param cldrJSONData - default CLDR data to load
     */
    public initialize(langVo: LanguageVO, texts: any, cldrJSONData?: any): void {
        this._initialized = true;
        this.languageVO = langVo;

        Globalize.load(
            // here we pass an array of json object containing all the localizations
            cldrJSONData || CLDRJSONData
        );

        Globalize.locale(this._languageVO.locale);

        if (!this.loadedLocales.has(this._languageVO.locale)) {
            this.loadedLocales.add(this._languageVO.locale);
        }

        this.changeNumberFormatters();

        if (texts != null) {
            this.setTexts(texts);
        } else {
            logger.warn("the json translations file is empty");
        }

        this.clearCaches();
    }

    /**
     * loads new CLDR (globalize) and translation (localize) data
     * @param languageVO {LanguageVO} the language value object configuration
     * @param texts {any} the localization translation JSON
     */
    public setLanguageAsync(langVo: LanguageVO, texts: any): Promise<any> {
        // Potential bug, we are only setting the language code not the countryCode
        this.languageVO = langVo;
        return new Promise((resolve, reject) => {
            if (!this._initialized) {
                reject("the default CLDR data is not loaded : en, de, supplemental");
            }
            this.setLocaleAsync(this._languageVO.locale)
                .then(() => {
                    // tslint:disable-next-line:no-unused-expression
                    texts && this.setTexts(texts);
                    this.changeNumberFormatters();
                    resolve();
                })
                .catch(error => {
                    reject("The CLDR JSON failed: " + error);
                });
        });
    }

    /**
     * If we change the locale we should also make sure the CLDR data is available for this locale and load it
     * @param locale {LocaleID} the language id to change
     */
    public setLocaleAsync(locale: string): Promise<any> {
        this.clearCaches();
        return new Promise((resolve, reject) => {
            if (!this.loadedLocales.has(locale)) {
                this.downloadCLDRAsync(locale)
                    .then(resolve)
                    .catch(reject);
            } else {
                Globalize.locale(locale);
                resolve();
            }
        });
    }

    /**
     * Finalize - tidy up for GC
     */
    public finalize(): void {
        if (this._initialized) {
            this._initialized = false;
            this.languageVO = null;
            this.clearCaches();
        }
    }


    public getLocale(): string {
        logger.debug("this.languageVO.locale ", this._languageVO.locale, "  Globalize.locale ", Globalize.locale().locale);
        return this._languageVO.locale;
    }


    public getTimeZone(): string {
        logger.warn("getTimeZone: not implemented");
        // if needed use Globalize.loadTimeZone( ianaTzData ) ?
        return "";
    }

    /**
     * Convert object properties to lowercase
     * @param obj
     */
    public objectToLowerCase(obj: any): any {
        return Object.keys(obj).reduce((collection, key) => ((collection[key.toLowerCase()] = obj[key]), collection), {});
    }

    /**
     * Set the translation dataand load into Globalize messges property
     * @param texts The translation JSON data
     */
    public setTexts(texts: LanguageData): void {
        this._textsVersionNo = TextsParser.parseVersionNo(texts);
        this._textsDeployTime = TextsParser.parseDeployTime(texts);
        texts = this._isCaseSensitive ? texts : this.objectToLowerCase(texts);


        let translations = JSON.stringify(texts);
        if (this._languageVO.isLanguageWrittenRightToLeft) {
            translations = reversePunctuation(translations);
        }

        let json = JSON.parse(translations);
        delete json["@metadata"];


        let translation = {};
        translation[this._languageVO.locale] = json;
        Globalize.loadMessages(translation);
    }

    /**
     * @private
     */
    // tslint:disable-next-line:no-empty
    public setData(data: any): void {
    }

    /**
     * @private
     */
    public hasText(textID: string): boolean {
        if (typeof textID !== "string") {
            return false;
        }

        const id: string = this._isCaseSensitive ? textID : textID.toLowerCase();
        return !!Globalize.cldr.get(["globalize-messages/" + this._languageVO.locale, id]);
    }

    /**
     * @private
     */
    public getText(textID: string, values: any = null): string {
        if (typeof textID !== "string") {
            return textID;
        }
        const id: string = this._isCaseSensitive ? textID : textID.toLowerCase();

        let text: string = this.cachedTranslations.get(id);


        if (!text) {
            text = Globalize.cldr.get(["globalize-messages/" + this._languageVO.locale, id]) || "";
            if (text) {
                this.cachedTranslations.put(id, text);
            }
        }
        return doReplacements(values, text,   this._languageVO.isLanguageWrittenRightToLeft);  // due to a nasty bug HTML-4947 HTML-4953 we need to reverse the placeholders only in arabic and only when placeholders are at the beginning/end of sentence ( depending on BIDI algorythm)
    }

    public isEmpty(obj) {
        return obj === null || Object.keys(obj).length === 0;
    }

    /**
     * in our translations replacements are indexes  ( my name is {0} and my surname is {1} )
     * repl  is  normally an array  with all the replacements   ["john", "doe"]
     * i dont know if in our code base there are edgecases where we have named placeholder or we are passing Objects (with number props   {0:"john", 1:"doe"}) therefore repl has type any
     * problem is if we by mistake pass a string...  each char of the string will be treaten as a single value to be replaced..     (todo improve and restrict usages, better typing)
     *
     * a replacement can be ANYTHING,   a string, a number or even another AbstractTextContentVO  which will itself trigger another Localization
     * @private
     */
    public text(textId: string, repl: any = null, useGrouping: boolean = true): string {
        let result: string;
        let finalReplacements: any = {};

        if (!this.isEmpty(repl)) {
            let name: string;
            let value: any;

            // tslint:disable-next-line:forin
            for (name in repl) {
                if (name.length === 0) {
                    finalReplacements[name] = "";
                    continue;
                }

                value = repl[name];

                if (value instanceof AbstractTextContentVO) {
                    finalReplacements[name] = (<AbstractTextContentVO>value).compose();
                    continue;
                }

                if (!this._localizeReplacements) {
                    finalReplacements[name] = value;
                    continue;
                }
                //  empire localizes replacements as well...
                //
                if (value === "") {
                    finalReplacements[name] = value;
                    continue;
                }

                // if value passed as replacement is a string  THAT STRING WILL BE FIRST SEARCHED IN CASE IT IS LOCALIZABLE TEXTID AS WELL..
                if (isNaN(Number(value))) {
                    // localizing nested textID
                    finalReplacements[name] = this.getText(value) || value;
                    continue;
                }

                // if it can be casted as a Number localize it
                /**
                 * 1. Activate forceRTLFormatting due to this is a textParameter so it's a number within continuing text
                 * TODO: fix this if a second rtl language is introduced, which is not supposed to use kilo abbrev in text params
                 * 2. when paramValue is already a formatted string eg. 1.700 it will be converted to a
                 * wrong number in this case 1.7
                 * */
                //  if replacement is a number
                finalReplacements[name] = this.number(
                    Number(value),
                    !this._languageVO.isLanguageWrittenRightToLeft,
                    -1,
                    this._languageVO.isLanguageWrittenRightToLeft,
                    useGrouping
                );
            }
        }

        if (typeof textId === "string" && textId !== "") {
            result = this.getText(textId, repl ? finalReplacements : null);
            if (!result) {
                // If text is empty set textID as text
                result = textId;
            }
        } else {
            result = "";
        }

        return result;
    }


    /**
     * @private
     */
    // tslint:disable-next-line:max-line-length
    public number(value: number,
                  compact: boolean = false,
                  fractionalDigits: number = -1,
                  isRTL: boolean = false,
                  useGrouping: boolean = true): string {
        let result: string;
        let neededFraction: number;
        let numOfDecimalDigits: number = value.toString().substring(value.toString().indexOf(".") + 1).length;
        if (numOfDecimalDigits === value.toString().length) {
            neededFraction = 0;
        } else {
            neededFraction = numOfDecimalDigits;
        }

        // this._numberFormatter.leadingZero = this._languageVO.leadingZero;
        // this._numberFormatter.trailingZeros = this._languageVO.trailingZeros;

        let abbreviate: boolean = compact && !this._languageVO.neverUseAbbreviations;
        let fractionalDigitsToUse: number = neededFraction;

        if (fractionalDigits > -1) {
            fractionalDigitsToUse = fractionalDigits;
        } else if (this._languageVO.fractionalDigits > -1) {
            fractionalDigitsToUse = this._languageVO.fractionalDigits;
        }

        let minFractionalDigitsToUse: number = !this._languageVO.trailingZeros ? 0 : fractionalDigitsToUse;
        this._numberFormatter = Globalize.numberFormatter({
            maximumFractionDigits: fractionalDigitsToUse,
            minimumFractionDigits: minFractionalDigitsToUse,
            useGrouping: useGrouping
        });

        if (abbreviate) {
            result = this.shortenLargeNumber(value, isRTL);
        } else {
            result = this._numberFormatter(value);
        }

        // Remove of the leading zero
        if (!this._languageVO.leadingZero) {
            result = result.replace(/^0+/, "");
        }
        return result;
    }

    public integer(value: number): string {
        return this._integerFormatter(value);
    }

    public decimal(value: number): string {
        return this._decimalFormatter(value);
    }

    public updateCurrencyFormatter(currencyCode: string, currencySymbol: string): void {
        if (currencyCode !== this._currencyCode || currencySymbol !== this._currencySymbol) {
            let hasSymbol = !/^[A-Z]{3}$/.test(currencySymbol);
            this._currencyFormatter = Globalize.currencyFormatter( currencyCode, {
                style: hasSymbol ? "symbol" : "code",
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            } );
        }
    }
    public formateCurrency(value: number, withCurrencySymbol: boolean = false): string {
        return this._currencyFormatter(value);
    }

    /**
     * @private
     */
    public datetime(date: Date, dateStyle: string = DateTimeStyle.SHORT, timeStyle: string = DateTimeStyle.SHORT): string {
        if (!date) {
            date = new Date();
            date.setTime(0);
            // this.logger.error(FlashProcessor.ERROR_NULL, ["date"]);
        }
        // let result = [] ;
        let result: string = "";
        if (dateStyle !== DateTimeStyle.NONE) {
            // result.push(Globalize.formatDate(date, {date: this.dateTimeStyleMapping(dateStyle)}));
            result = Globalize.formatDate(date, {date: this.dateTimeStyleMapping(dateStyle)});
        }
        if (timeStyle !== DateTimeStyle.NONE) {
            if (result.length > 0) {
                result = result + " " + Globalize.formatDate(date, {time: this.timeStyleMapping(timeStyle)});
            } else {
                result = result + Globalize.formatDate(date, {time: this.timeStyleMapping(timeStyle)});
            }
        }
        return result;
    }

    public digitalClock(time: string): string {
        if (this._languageVO.isLanguageWrittenRightToLeft) {
            return localizeTimeString(time, this._integerFormatter);
        }
        return time;
    }

    /**
     * Download a new languageId from the server, example downloadLocaleAsync('es')
     * @param locale string - the new languageId to download using XHR
     */
    private downloadCLDRAsync(locale: string): Promise<any> {
        const url = `${this._cldrURL}/${locale}.json`;
        return new Promise((resolve, reject) => {
            let loader = new URLLoaderService(url);
            loader
                .load()
                .then(JSON.parse)
                .then(response => {
                    Globalize.load(response); // load all the CLDR data from the fetch response
                    Globalize.locale(locale); // set the current locale to languageId
                    this.loadedLocales.add(locale); // save a cache of the loaded locales*/
                    resolve();
                })
                .catch(reject);
        });
    }

    /**
     *  https://github.com/globalizejs/globalize/blob/master/doc/api/date/date-formatter.md
     * @param originaValue
     * @returns {string}
     */
    private dateTimeStyleMapping(originaValue: string): string {
        let mappedValue: string = "none";

        switch (originaValue) {
            case DateTimeStyle.FULL:
                mappedValue = "full";
                break;
            case DateTimeStyle.LONG:
                mappedValue = "long";
                break;
            case DateTimeStyle.MEDIUM:
                mappedValue = "medium";
                break;
            case DateTimeStyle.SHORT:
                mappedValue = "short";
                break;
            case DateTimeStyle.CUSTOM:
                mappedValue = "custom";
                break;
            default:
                mappedValue = originaValue;
        }

        return mappedValue;
    }

    /**
     * @private
     * @param value
     * @param fractionalDigits
     * @param isRTL
     * @return
     */
    private shortenLargeNumber(value: number, isRTL: boolean): string {
        let units: string[] = ["generic_kForThousand", "generic_mForMillion"];
        let result: string = value.toString();
        let decimal: number = 0;

        if (value < this._languageVO.abbreviationThreshold) {
            result = this._numberFormatter(value);
        } else {
            for (let i: number = units.length - 1; i >= 0; i--) {
                decimal = Math.pow(1000, i + 1);

                if (value <= -decimal || value >= decimal) {
                    result = this._numberFormatter(value / decimal) + (isRTL ? " " : "") + this.getText(units[i]);
                    break;
                }
            }
        }

        return result;
    }

    private clearCaches(): void {
        this.cachedTranslations.clear();
    }

    /**
     * Create the integerFormatter and deciamlFormatter for currect locale.
     * This is an expensive operation so only do it when changing language.
     * This is why integer() and deciaml() are faster than number(), number() needs to create a formatter with each call.
     */
    private changeNumberFormatters() {
        this._integerFormatter = Globalize.numberFormatter({
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
            useGrouping: true
        });
        this._decimalFormatter = Globalize.numberFormatter({
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            useGrouping: false
        });
    }

    private timeStyleMapping(originaValue: string): string {
        let mappedValue: string = "none";

        switch (originaValue) {
            case DateTimeStyle.FULL:
                mappedValue = "full";
                break;
            case DateTimeStyle.LONG:
                mappedValue = "long";
                break;
            case DateTimeStyle.MEDIUM:
                mappedValue = "medium";
                break;
            case DateTimeStyle.SHORT:
                mappedValue = "short";
                break;
            case DateTimeStyle.CUSTOM:
                mappedValue = "custom";
                break;
            case DateTimeStyle.FULL:
                mappedValue = "medium";
                break;
            default:
                mappedValue = originaValue;
        }

        return mappedValue;
    }


    public get isArabic(): boolean {
        return this._languageVO.isLanguageWrittenRightToLeft;
    }

    /**
     * @private
     */
    public get processorType(): string {
        return ProcessorTypeConstants.PROCESSOR_GLOBALIZE;
    }

    /**
     * @private
     */
    public get initialized(): boolean {
        return this._initialized;
    }

    /**
     * @private
     */
    public get localizeReplacements(): boolean {
        return this._localizeReplacements;
    }

    /**
     * @private
     */
    public set localizeReplacements(value: boolean) {
        this._localizeReplacements = value;
    }

    /**
     * @private
     */
    public get textsVersionNo(): string {
        return this._textsVersionNo;
    }

    /**
     * @private
     */
    public get textsDeployTime(): string {
        return this._textsDeployTime;
    }
}


const localizeTimeString = (time: string, formatter: any, rtl: boolean = true): string => {
    const formatted = time.split(":")
        .map(num => formatter(parseInt(num)));
    if (rtl) {
        formatted.reverse()
    }
    return formatted.join(":");
}


/**
 * extracted function for easier testing

 * @param values
 * @param text
 * @returns {string}
 */
export const doReplacements = (values: any, text: string, reverse:boolean = true) => {
    if (text) {
        const replacements = values && Object.keys(values);
        if (replacements) {
          //  logger.debug("TEXT before replacements ", text);
            // generating a formatter is too expensive for us, a simple string replace satisfies us
            // text = Globalize.messageFormatter(id)(values);
        /*
          bug HTML-4947 HTML-4953
          for some reason when placehoder is at the end of the sentence in english, (and therefore is flipped to the right in arabic)  regex works fine but only numbers are correctly displayed while strings stay on the left
          Castle Davide --->  should become Davide Castle (with castle written in arab letters - reading from right)    BUT it is still displayed as Castle Davide
          ** !!  when looking at the Text string before the replacement the placeholder looks completely broken   "المستوى {0}",   this is just a display thing.   placeholder replacment works fine. ( beside this specific behaviour for strings)
          Level 100 -->  100 Level (with level and digits written in arab letters - reading from right)

          we need to reverse the placeholders only in arabic and only when placeholders are at the beginning/end of sentence ( depending on BIDI algorythm)
          */
            if (reverse && replacements.length == 1 && text) { //  && typeof values[replacements[0]] == "string"    // && text.endsWith("}")
                text = reverseSinglePlaceholder(text);
            }
            replacements.forEach(key => {
                let v = values[key];
                let regx = new RegExp(`\\{${key}}`, "ig");
                text = text.replace(regx, v);
            });
         //   logger.debug("TEXT after replacements ", text);
        }
    }
    return text;
}


